<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.root"
  >
    <div
      :disabled="disabled"
      :class="$style.container"
      :style="style.container"
    >
      <template v-for="(option, index) in options">
        <div
          :key="index"
          :disabled="disabled"
          :selected="selectedIndex === index"
          @mousedown="selectOption(index)"
          @mouseup="$emit('select', selectedOptionId)"
          :class="$style.option"
          :style="style.option"
        >
          <slot
            name="option"
            :index="index"
            :option="option"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import { CssStyleProcessor } from '@js/vue-style-utils'
  import { KeyError } from '@js/exceptions'

  export default {
    name: 'BaseDialobueOption',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change',
    },
    props: {
      vmodelValue: {
        type: [Number, Object, String],
        required: false,
        default: null,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      disableDeselect: {
        type: Boolean,
        required: false,
        default: false,
      },
      idKey: {
        type: String,
        required: false,
        default: 'id',
      },
      options: {
        type: Array,
        required: false,
        default: () => [],
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        selectedIndex: this.getSelectedIndex(this.vmodelValue),
        selectedOptionId: null,
        style: {
          container: {},
          option: {},
          root: {},
        },
      };
    },
    created: function () {
      this.selectedOptionId = this.selectedIndex === null
        ? null
        : this.options[this.selectedIndex][this.idKey];

      const proc = new CssStyleProcessor(
        '--v2-base-dialogue-option',
        ['selected'],
      );

      Object.assign(
        this.style.container,
        proc.getExtendedStyle(this.styleConfig.container, 'container'),
      );
      Object.assign(
        this.style.option,
        proc.getExtendedStyle(this.styleConfig.option, 'option'),
      );

      Object.assign(
        this.style.root,
        this.styleConfig.root,
      );
    },
    methods: {
      getSelectedIndex: function (vmodelValue) {
        this.validateIdKey();
        const index = this.options
          .findIndex(elem => elem[this.idKey] === vmodelValue);
        return index < 0 ? null : index;
      },
      selectOption: function (index) {
        this.selectedIndex = (
          !this.disableDeselect && this.selectedIndex === index)
            ? null
            : index;
        //this.selectedIndex = this.selectedIndex === index ? null : index;

        this.selectedOptionId = this.selectedIndex === null
          ? null
          : this.options[this.selectedIndex][this.idKey];
        //this.$emit('change', selectedOptionId);
      },
      validateIdKey: function () {
        if (!this.options.every(obj => this.idKey in obj)) {
          throw new KeyError('The specified idKey not found in given options.');
        }
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.selectedIndex = this.getSelectedIndex(newVal);
      },
      idKey: function (newVal, oldVal) {
        this.validateIdKey(newVal);
      },
      selectedOptionId: function (newVal, oldVal) {
        this.$emit('change', newVal);
      },
    },
  };
</script>

<style lang="stylus" module>
  .container
  background-color var(--v2-base-dialogue-option-container-background-color)
  border var(--v2-base-dialogue-option-container-border)
  border-radius var(--v2-base-dialogue-option-container-border-radius)
  border-width var(--v2-base-dialogue-option-container-border-width)
  box-shadow var(--v2-base-dialogue-option-container-box-shadow)
  &[selected]
    background-color var(--v2-base-dialogue-option-container-background-color-selected)
    border var(--v2-base-dialogue-option-container-border-selected)
    border-radius var(--v2-base-dialogue-option-container-border-radius-selected)
    border-width var(--v2-base-dialogue-option-container-border-width-selected)
    box-shadow var(--v2-base-dialogue-option-container-box-shadow-selected)
  &:hover
    background-color var(--v2-base-dialogue-option-container-background-color-hover)
    border var(--v2-base-dialogue-option-container-border-hover)
    border-radius var(--v2-base-dialogue-option-container-border-radius-hover)
    border-width var(--v2-base-dialogue-option-container-border-width-hover)
    box-shadow var(--v2-base-dialogue-option-container-box-shadow-hover)
  &:active
    background-color var(--v2-base-dialogue-option-container-background-color-active)
    border var(--v2-base-dialogue-option-container-border-active)
    border-radius var(--v2-base-dialogue-option-container-border-radius-active)
    border-width var(--v2-base-dialogue-option-container-border-width-active)
    box-shadow var(--v2-base-dialogue-option-container-box-shadow-active)
  &[disabled]
    background-color var(--v2-base-dialogue-option-container-background-color-disabled)
    border var(--v2-base-dialogue-option-container-border-disabled)
    border-radius var(--v2-base-dialogue-option-container-border-radius-disabled)
    border-width var(--v2-base-dialogue-option-container-border-width-disabled)
    box-shadow var(--v2-base-dialogue-option-container-box-shadow-disabled)

  .option
    background-color var(--v2-base-dialogue-option-option-background-color)
    border var(--v2-base-dialogue-option-option-border)
    border-radius var(--v2-base-dialogue-option-option-border-radius)
    border-width var(--v2-base-dialogue-option-option-border-width)
    box-shadow var(--v2-base-dialogue-option-option-box-shadow)
    color var(--v2-base-dialogue-option-option-color)
    cursor var(--v2-base-dialogue-option-option-cursor)
    font-family var(--v2-base-dialogue-option-option-font-family)
    font-size var(--v2-base-dialogue-option-option-font-size)
    font-weight var(--v2-base-dialogue-option-option-font-weight)
    &[selected]
      background-color var(--v2-base-dialogue-option-option-background-color-selected)
      border var(--v2-base-dialogue-option-option-border-selected)
      border-radius var(--v2-base-dialogue-option-option-border-radius-selected)
      border-width var(--v2-base-dialogue-option-option-border-width-selected)
      box-shadow var(--v2-base-dialogue-option-option-box-shadow-selected)
      color var(--v2-base-dialogue-option-option-color-selected)
      cursor var(--v2-base-dialogue-option-option-cursor-selected)
      font-family var(--v2-base-dialogue-option-option-font-family-selected)
      font-size var(--v2-base-dialogue-option-option-font-size-selected)
      font-weight var(--v2-base-dialogue-option-option-font-weight-selected)
    &:hover
      background-color var(--v2-base-dialogue-option-option-background-color-hover)
      border var(--v2-base-dialogue-option-option-border-hover)
      border-radius var(--v2-base-dialogue-option-option-border-radius-hover)
      border-width var(--v2-base-dialogue-option-option-border-width-hover)
      box-shadow var(--v2-base-dialogue-option-option-box-shadow-hover)
      color var(--v2-base-dialogue-option-option-color-hover)
      cursor var(--v2-base-dialogue-option-option-cursor-hover)
      font-family var(--v2-base-dialogue-option-option-font-family-hover)
      font-size var(--v2-base-dialogue-option-option-font-size-hover)
      font-weight var(--v2-base-dialogue-option-option-font-weight-hover)
    &:active
      background-color var(--v2-base-dialogue-option-option-background-color-active)
      border var(--v2-base-dialogue-option-option-border-active)
      border-radius var(--v2-base-dialogue-option-option-border-radius-active)
      border-width var(--v2-base-dialogue-option-option-border-width-active)
      box-shadow var(--v2-base-dialogue-option-option-box-shadow-active)
      color var(--v2-base-dialogue-option-option-color-active)
      cursor var(--v2-base-dialogue-option-option-cursor-active)
      font-family var(--v2-base-dialogue-option-option-font-family-active)
      font-size var(--v2-base-dialogue-option-option-font-size-active)
      font-weight var(--v2-base-dialogue-option-option-font-weight-active)
    &[disabled]
      background-color var(--v2-base-dialogue-option-option-background-color-disabled)
      border var(--v2-base-dialogue-option-option-border-disabled)
      border-radius var(--v2-base-dialogue-option-option-border-radius-disabled)
      border-width var(--v2-base-dialogue-option-option-border-width-disabled)
      box-shadow var(--v2-base-dialogue-option-option-box-shadow-disabled)
      color var(--v2-base-dialogue-option-option-color-disabled)
      cursor var(--v2-base-dialogue-option-option-cursor-disabled)
      font-family var(--v2-base-dialogue-option-option-font-family-disabled)
      font-size var(--v2-base-dialogue-option-option-font-size-disabled)
      font-weight var(--v2-base-dialogue-option-option-font-weight-disabled)
</style>
